<template>
    <form class="max-w-7xl grid grid-cols-7 gap-6 mx-auto" @submit="submit" action="/booking/save" method="post" ref="form">
        <input type="hidden" name="_token" :value="csrf">
        <input type="hidden" name="destination" :value="booking.destination">
        <input type="hidden" name="returning_flight_number" :value="booking.returningFlightNumber">
        <input type="hidden" name="departure_flight_number" :value="booking.departureFlightNumber">
        <input type="hidden" name="selected_inbound_flight" :value="selectedInboundFlight ? JSON.stringify(selectedInboundFlight) : ''">
        <input type="hidden" name="selected_outbound_flight" :value="selectedOutboundFlight ? JSON.stringify(selectedOutboundFlight) : ''">
        <div class="col-span-7 md:col-span-5 space-y-6">
           <div class="mt-4 lg:mt-12">
                <div :class="'bg-'+colourTheme" class="py-4 text-2xl text-white px-3 md:px-6">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 inline-block mb-1" viewBox="0 0 20 20"
                        fill="currentColor">
                        <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clip-rule="evenodd" />
                    </svg>

                    Booking Information
                </div>
                <div class="bg-white p-8 border-b-2 border-gray-300 border-opacity-70">
                    <div class="grid grid-cols-4 gap-6">
                        <div class="col-span-4 mb-4">
                            <p class="text-lg">
                                Your booking has been confirmed.  You will receive a confirmation email with your booking details shortly. The below details will be required when you arrive at the carpark, so why not speed up your check-in now by providing the remaining details below.
                            </p>
                        </div>
                        <div class="col-span-4 md:col-span-2">
                            <p class="font-bold">Booking Reference(s)</p>
                            <p v-for="reference in references" :key="reference">{{ reference }}</p>
                        </div>

                        <div class="col-span-4 md:col-span-2">
                            <p class="font-bold">Last Name</p>
                            <span>{{ booking.customer.last_name }}</span>
                        </div>

                        <div class="col-span-4 md:col-span-2">
                            <p class="font-bold">Email</p>
                            <span>{{ booking.customer.email }}</span>
                        </div>

                        <div class="col-span-4 md:col-span-2">
                            <p class="font-bold">Phone</p>
                            <span>{{ booking.customer.phone }}</span>
                        </div>

                        <div class="col-span-4 md:col-span-2" v-if="booking.special_requests">
                            <p class="font-bold">Special Requests</p>
                            <span>{{ booking.special_requests }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-12">
                <div :class="'bg-'+colourTheme" class="py-4 text-2xl text-white px-6">
                    <i class="fas fa-car"></i>

                    Vehicle Information
                </div>
                <div class="bg-white p-4 md:p-8">
                    <vehicle-booking-form
                        v-for="(vehicle, index) in vehicles"
                        :key="vehicle"
                        :index="index"
                        v-model="vehicles[index]"
                        :colour-theme="colourTheme"
                        :errors="errors"
                    />
                </div>
            </div>

            <div class="mt-12">
                <div :class="'bg-'+colourTheme" class="py-4 text-2xl text-white px-6">
                    <i class="fas fa-plane"></i>

                    Flight Information
                </div>
                <div class="bg-white p-8 border-b-2 border-gray-300 border-opacity-70">
                    <div class="grid grid-cols-4 gap-x-6">
                        <div class="col-span-4">
                            <p class="text-gray-700 text-sm">Add your return flight so we know when to expect you back at Edinburgh — it speeds up check-in.</p>
                        </div>
                        
                        <!-- Return Flight Number -->
                        <div class="col-span-4 md:col-span-2 mt-6">
                            <flight-search-input
                                label="Return Flight Number"
                                v-model="booking.returningFlightNumber"
                                name="returning_flight_number"
                                styling="uppercase"
                                flight-type="inbound"
                                :entry-time="entry"
                                :exit-time="exit"
                                airport-code="EDI"
                                @flight-selected="onInboundFlightSelected"
                            ></flight-search-input>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-span-7 md:col-span-2 space-y-6">
            <!-- <login-card v-if="$dataLayer.user == null" :theme="colourTheme" title="Already Registered?"></login-card> -->
            <div class="mt-4 lg:mt-12">
                <SummaryCard
                    :originalTotal="this.total"
                    :newTotal="sessionData.total"
                    :total="this.total"
                    :carpark="carpark"
                    :vehicles="vehicles"
                    :entry="new Date(sessionData.entry)"
                    :exit="new Date(sessionData.exit)"
                    :show-save-btn="true"
                    :product="product"
                ></SummaryCard>
            </div>
        </div>
    </form>
</template>

<script>

import axios from 'axios';
import moment from 'moment';
import VehicleBookingForm from './VehicleBookingForm.vue';
import TextInput from '../TextInput.vue';
import FlightDropdown from '../FlightDropdown.vue';
import FlightSearchInput from '../FlightSearchInput.vue';
import SummaryCard from './Booking/SummaryCard.vue';

export default {
    data() {
        return {
            booking: {
                departureFlightNumber: null,
                returningFlightNumber: null,
                outboundFlightId: null,
                inboundFlightId: null,
                destination: null,
                destinationCode: null,
                customer: {
                    first_name: null,
                    last_name: null,
                    email: null,
                    phone: null,
                },
                partySize: null,
                special_requests: null,
            },
            vehicles: [{
                registration: null,
                type: null,
                make: null,
                model: null,
                colour: null,
                selectedValet: null,
            }],
            total: 1,
            errors: [],
            submitted: false,
            moment,
            csrf: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            selectedOutboundFlight: null,
            selectedInboundFlight: null,
        };
    },
    components: {
        SummaryCard,
        VehicleBookingForm,
        TextInput,
        FlightDropdown,
        FlightSearchInput,
    },
    computed: {
    },
    props: {
        carpark: { type: String, required: true },
        airport: { type: String, required: true },
        colourTheme: { type: String, required: true },
        entry: { type: String, required: true },
        exit: { type: String, required: true },
        sessionData: { type: Object, required: false },
        references: { required: true },
        product: { required: true },
    },
    methods: {
        selectedValetOption(option, vehicleIndex) {
            this.vehicles[vehicleIndex].selectedValet = option;
        },
        onOutboundFlightSelected(flightData) {
            this.selectedOutboundFlight = flightData;
            if (flightData) {
                this.booking.departureFlightNumber = flightData.flight_iata;
            }
        },
        onInboundFlightSelected(flightData) {
            this.selectedInboundFlight = flightData;
            if (flightData) {
                this.booking.returningFlightNumber = flightData.flight_iata;
            }
        },
        onDestinationChanged(destinationCode) {
            this.booking.destinationCode = destinationCode;
            // Clear inbound flight when destination changes
            this.booking.inboundFlightId = null;
            this.selectedInboundFlight = null;
            
            // Set destination name (you might want to add a mapping for better names)
            this.booking.destination = this.getDestinationName(destinationCode);
        },
        getDestinationName(code) {
            // Map common airport codes to city names
            const airportNames = {
                'LHR': 'London Heathrow',
                'LGW': 'London Gatwick',
                'STN': 'London Stansted',
                'MAN': 'Manchester',
                'BHX': 'Birmingham',
                'DUB': 'Dublin',
                'AMS': 'Amsterdam',
                'CDG': 'Paris Charles de Gaulle',
                'BCN': 'Barcelona',
                'MAD': 'Madrid',
                'FCO': 'Rome',
                'MLA': 'Malta',
                'PMI': 'Palma',
                'AGP': 'Malaga',
                'FAO': 'Faro',
                'OPO': 'Porto',
                'PRG': 'Prague',
                'BUD': 'Budapest',
                'WAW': 'Warsaw',
                'ARN': 'Stockholm',
                'CPH': 'Copenhagen',
                'OSL': 'Oslo',
                'HEL': 'Helsinki'
            };
            return airportNames[code] || code;
        },
        submit() {
            return true;
            axios.post('/booking/save', {
                first_name: this.booking.customer.first_name,
                last_name: this.booking.customer.last_name,
                email: this.booking.customer.email,
                phone: this.booking.customer.phone,
                returningFlightNumber: this.booking.returningFlightNumber,
                departureFlightNumber: this.booking.departureFlightNumber,
                selected_outbound_flight: this.selectedOutboundFlight,
                selected_inbound_flight: this.selectedInboundFlight,
                entry: this.entry,
                exit: this.exit,
                destination: this.booking.destination,
                carpark: this.carpark,
                partySize: this.booking.partySize,
                special_requests: this.booking.special_requests,
                vehicles: this.vehicles,
            }).then((response) => {
                console.log(response);

                if (response.data.success) {
                    window.location.href = response.data.redirect_url;
                }
            }).catch((e) => {
                console.log(e.response.data.errors);
                this.errors = e.response.data.errors ?? [];
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth',
                });
            });
        },
    },
    watch: {
        vehicles: {
            immediate: true,
            deep: true,
            handler(value) {
                this.total = 0;
                if (value?.length === 0) return;

                value?.forEach((vehicle) => {
                    this.total += (vehicle.price ?? 0);

                    if (vehicle.selectedValet != null) {
                        this.total += vehicle.selectedValet.price;
                    }
                });
            },
        },
    },
    created() {
        if (typeof this.sessionData !== 'undefined') {
            const booking = this.sessionData;
            this.booking.customer.first_name = booking.first_name;
            this.booking.customer.last_name = booking.last_name;
            this.booking.customer.email = booking.email;
            this.booking.customer.phone = booking.phone;

            this.booking.partySize = booking.party_size;
            this.booking.special_requests = booking.special_requests;

            this.booking.returningFlightNumber = booking.returning_flight_number;
            this.booking.destination = booking.destination;

            if (typeof booking.actual_vehicle !== 'undefined') {
                this.vehicles = booking.actual_vehicle;

                booking.vehicles?.forEach((vehicle, index) => {

                    this.total = vehicle.type.adjusted;

                    this.vehicles[index].registration = vehicle.registration;
                    this.vehicles[index].type = vehicle.type;
                    this.vehicles[index].make = vehicle.make;
                    this.vehicles[index].model = vehicle.model;
                    this.vehicles[index].colour = vehicle.colour;
                    this.vehicles[index].selectedValet = vehicle.selectedValet;
                });
            }
        }
    },
};
</script>
